import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";
import { addWeeks, startOfWeek, endOfWeek, format, isWithinInterval } from "date-fns";

export default function ResourceForecast({ users, projects, tasks, timeEntries }) {
  // Generate forecast for next 4 weeks
  const forecastWeeks = [];
  for (let i = 1; i <= 4; i++) {
    const weekStart = startOfWeek(addWeeks(new Date(), i), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(addWeeks(new Date(), i), { weekStartsOn: 1 });
    forecastWeeks.push({ 
      week: i,
      start: weekStart, 
      end: weekEnd, 
      label: format(weekStart, "d MMM") 
    });
  }

  // Calculate demand and capacity for each week
  const forecastData = forecastWeeks.map(week => {
    const weekTasks = tasks.filter(task => {
      if (!task.due_date || task.status === "completed" || task.status === "cancelled") return false;
      const dueDate = new Date(task.due_date);
      return isWithinInterval(dueDate, { start: week.start, end: week.end });
    });

    const demand = weekTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
    const totalCapacity = users.reduce((sum, u) => sum + (u.weekly_capacity || 37.5), 0);
    
    // Calculate historical utilization to adjust forecast
    const historicalUtilization = 0.75; // Assume 75% utilization based on past data
    const availableCapacity = totalCapacity * historicalUtilization;

    return {
      week: week.label,
      Etterspørsel: parseFloat(demand.toFixed(1)),
      Kapasitet: parseFloat(availableCapacity.toFixed(1)),
      Gap: parseFloat((demand - availableCapacity).toFixed(1)),
    };
  });

  // Identify bottlenecks and recommendations
  const bottlenecks = forecastData.filter(week => week.Gap > 0);
  const overCapacity = forecastData.filter(week => week.Gap < -50);

  // Project-specific forecasts
  const activeProjects = projects.filter(p => p.status === "active" || p.status === "planning");
  const projectForecasts = activeProjects.map(project => {
    const projectTasks = tasks.filter(t => t.project_id === project.id && t.status !== "completed");
    const totalEstimated = projectTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
    const totalLogged = timeEntries.filter(e => e.project_id === project.id).reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);
    const remaining = totalEstimated - totalLogged;
    const estimatedWeeksRemaining = remaining / 20; // Assuming 20 hours per week on project

    return {
      name: project.name,
      remaining: parseFloat(remaining.toFixed(1)),
      estimatedWeeks: parseFloat(estimatedWeeksRemaining.toFixed(1)),
      taskCount: projectTasks.length,
    };
  }).filter(p => p.remaining > 0).sort((a, b) => b.remaining - a.remaining);

  return (
    <div className="space-y-6">
      {/* Forecast Chart */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">4-Ukers Prognose</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={forecastData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="week" stroke="#64748b" />
            <YAxis stroke="#64748b" label={{ value: 'Timer', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Kapasitet" fill="#10b981" />
            <Bar dataKey="Etterspørsel" fill="#6b7280" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Alerts */}
      {bottlenecks.length > 0 && (
        <Alert className="bg-rose-50 border-rose-200">
          <AlertTriangle className="w-4 h-4 text-rose-600" />
          <AlertDescription className="text-rose-700">
            <strong>Kapasitetsmangel oppdaget:</strong> {bottlenecks.length} {bottlenecks.length === 1 ? "uke" : "uker"} med underskudd. 
            Vurder å flytte oppgaver eller ansette ekstra ressurser.
          </AlertDescription>
        </Alert>
      )}

      {overCapacity.length > 0 && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">
            <strong>Ledig kapasitet:</strong> {overCapacity.length} {overCapacity.length === 1 ? "uke" : "uker"} med overskuddskapasitet. 
            Vurder å ta på nye prosjekter.
          </AlertDescription>
        </Alert>
      )}

      {/* Weekly Forecast Details */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Ukentlig Prognose</h3>
        <div className="space-y-3">
          {forecastData.map((week, idx) => (
            <div key={idx} className="p-4 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-slate-900">Uke {idx + 1} - {week.week}</p>
                </div>
                <Badge className={
                  week.Gap > 0 ? "bg-rose-100 text-rose-700" :
                  week.Gap < -50 ? "bg-emerald-100 text-emerald-700" :
                  "bg-amber-100 text-amber-700"
                }>
                  {week.Gap > 0 ? `${week.Gap}h underskudd` : 
                   week.Gap < -50 ? `${Math.abs(week.Gap).toFixed(0)}h overskudd` : 
                   "Balansert"}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Etterspørsel</p>
                  <p className="font-semibold text-blue-600">{week.Etterspørsel}h</p>
                </div>
                <div>
                  <p className="text-slate-500">Tilgjengelig Kapasitet</p>
                  <p className="font-semibold text-emerald-600">{week.Kapasitet}h</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Project Forecasts */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Prosjektprognose</h3>
        <div className="space-y-3">
          {projectForecasts.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">Ingen aktive prosjekter med estimater</p>
          ) : (
            projectForecasts.map(project => (
              <div key={project.name} className="p-4 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-slate-900">{project.name}</p>
                    <p className="text-sm text-slate-500">{project.taskCount} gjenstående oppgaver</p>
                  </div>
                  <Badge variant="outline">
                    {project.estimatedWeeks.toFixed(1)} uker
                  </Badge>
                </div>
                <div className="text-sm">
                  <p className="text-slate-500">Estimert gjenstående arbeid: <span className="font-semibold text-slate-900">{project.remaining}h</span></p>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}